// Shared form used by both Create + Edit pages.

import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useT } from '../../i18n';
import { uploadOrDataUrl } from '../../lib/files-api';
import type {
  AnnouncementCategory,
  CreateAnnouncementInput,
  PublicAnnouncement,
} from '../../lib/announcements-api';

// Cover image is capped at 2 MB — rejected here so an oversized file never
// becomes a multi-MB data URL (R2-absent dev fallback) or hits the server.
const MAX_COVER_BYTES = 2 * 1024 * 1024;
const COVER_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

interface Props {
  initial?: PublicAnnouncement;
  submitting: boolean;
  onSubmit: (values: CreateAnnouncementInput) => Promise<void>;
  onCancel: () => void;
  /** When editing, hide the "publish_now" toggle — use the publish button on the list instead */
  isEdit?: boolean;
}

const CATEGORIES: Array<{ value: AnnouncementCategory; labelKey: string }> = [
  { value: 'OUTAGE', labelKey: 'ann.catOutage' },
  { value: 'MAINTENANCE', labelKey: 'ann.catMaintenance' },
  { value: 'SECURITY', labelKey: 'ann.catSecurity' },
  { value: 'GENERAL', labelKey: 'ann.catGeneral' },
];

export function AnnouncementForm({ initial, submitting, onSubmit, onCancel, isEdit = false }: Props) {
  const t = useT();
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreateAnnouncementInput>({
    defaultValues: {
      category: initial?.category ?? 'GENERAL',
      title: initial?.title ?? '',
      body_md: initial?.body_md ?? '',
      require_ack: initial?.require_ack ?? false,
      pinned: initial?.pinned ?? false,
      cover_image_url: initial?.cover_image_url ?? undefined,
      publish_now: !isEdit, // create defaults to publish_now=true; edit hides it
    },
  });

  const coverUrl = watch('cover_image_url');
  const coverRef = useRef<HTMLInputElement>(null);
  const [coverBusy, setCoverBusy] = useState(false);

  async function onPickCover(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type && !COVER_TYPES.includes(file.type)) {
      setError(t('ann.coverBadType'));
      return;
    }
    if (file.size > MAX_COVER_BYTES) {
      setError(t('ann.coverTooBig'));
      return;
    }
    setCoverBusy(true);
    setError(null);
    try {
      setValue('cover_image_url', await uploadOrDataUrl(file), { shouldDirty: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('ann.saveFailed'));
    } finally {
      setCoverBusy(false);
    }
  }

  return (
    <Box component="form" onSubmit={handleSubmit(async (v) => {
      setError(null);
      try {
        await onSubmit(v);
      } catch (e) {
        setError(e instanceof Error ? e.message : t('ann.saveFailed'));
      }
    })}>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={3}>
            <Typography variant="h5" fontWeight={600}>
              {isEdit ? t('ann.editTitle') : t('ann.newBtn')}
            </Typography>

            <Controller
              control={control}
              name="category"
              rules={{ required: t('ann.categoryRequired') }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('ann.fieldCategory')}
                  select
                  fullWidth
                  error={!!errors.category}
                  helperText={errors.category?.message ?? t('ann.categoryHelper')}
                >
                  {CATEGORIES.map((c) => (
                    <MenuItem key={c.value} value={c.value}>
                      {t(c.labelKey)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <TextField
              label={t('ann.fieldTitle')}
              fullWidth
              {...register('title', { required: t('ann.titleRequired'), maxLength: { value: 200, message: t('ann.titleMaxLen') } })}
              error={!!errors.title}
              helperText={errors.title?.message ?? t('ann.titleHelper')}
            />

            <TextField
              label={t('ann.fieldBody')}
              fullWidth
              multiline
              minRows={6}
              maxRows={20}
              {...register('body_md', { required: t('ann.bodyRequired') })}
              error={!!errors.body_md}
              helperText={errors.body_md?.message ?? t('ann.bodyHelper')}
            />

            <Stack spacing={1}>
              <Typography variant="subtitle2">{t('ann.coverImage')}</Typography>
              {coverUrl ? (
                <Box
                  sx={{
                    width: '100%', maxWidth: 360, aspectRatio: '16 / 9', borderRadius: 1,
                    overflow: 'hidden', bgcolor: 'grey.100',
                  }}
                >
                  <img src={coverUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
              ) : null}
              <input
                ref={coverRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                hidden
                onChange={onPickCover}
              />
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" disabled={coverBusy} onClick={() => coverRef.current?.click()}>
                  {coverBusy ? t('ann.coverWorking') : t('ann.coverUpload')}
                </Button>
                {coverUrl ? (
                  <Button
                    variant="text"
                    color="inherit"
                    disabled={coverBusy}
                    onClick={() => setValue('cover_image_url', undefined, { shouldDirty: true })}
                  >
                    {t('ann.coverRemove')}
                  </Button>
                ) : null}
              </Stack>
            </Stack>

            <Stack direction="row" spacing={3}>
              <FormControlLabel
                control={
                  <Controller
                    control={control}
                    name="pinned"
                    render={({ field }) => (
                      <Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />
                    )}
                  />
                }
                label={t('ann.pinToTop')}
              />
              <FormControlLabel
                control={
                  <Controller
                    control={control}
                    name="require_ack"
                    render={({ field }) => (
                      <Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />
                    )}
                  />
                }
                label={t('ann.requireAck')}
              />
            </Stack>

            {!isEdit ? (
              <FormControlLabel
                control={
                  <Controller
                    control={control}
                    name="publish_now"
                    render={({ field }) => (
                      <Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />
                    )}
                  />
                }
                label={
                  <span>
                    <strong>{t('ann.publishNow')}</strong>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {t('ann.saveDraft')}
                    </Typography>
                  </span>
                }
              />
            ) : null}

            {error ? <Alert severity="error">{error}</Alert> : null}

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={onCancel} disabled={submitting}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="contained" disabled={submitting}>
                {submitting ? t('common.saving') : isEdit ? t('ann.saveChanges') : t('ann.createBtn')}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
